import FileUploadInput from '@/components/FormFields/FileUpload/FileUploadInput.tsx';
import {
  GuidelineItem,
  GuidelinesManager,
} from '@/components/FormFields/GuidelinesManager.tsx';
import { SettingTitle } from '@/components/settings/SettingsTitle.tsx';
import { AuthContext } from '@/contexts/AuthContext.tsx';
import { DashboardContext } from '@/contexts/DashboardContext.tsx';
import { SnackbarContext } from '@/contexts/SnackbarContext.tsx';
import { UserContext } from '@/contexts/UserContext.tsx';
import { useAllowedFonts } from '@/hooks/useAllowedFonts';
import RefetchAccountDataModal from '@/modals/RefetchAccountDataModal.tsx';
import { scrapeAccountData } from '@/services/AccountsService.ts';
import { AccountBrand, AssetData } from '@superfunnel/types';
import ColorPickerField from '@superfunnel/ui-core/ColorPickerField';
import Autocomplete from '@superfunnel/ui-core/v2/Autocomplete';
import Button from '@superfunnel/ui-core/v2/Button';
import CodeInput from '@superfunnel/ui-core/v2/CodeInput';
import { isValidCss } from '@superfunnel/utils';
import { toCSS, toJSON } from 'css-convert-json';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { uid } from 'uid';

export const BrandStyles = () => {
  const allowedFonts = useAllowedFonts();
  const [edit, setEdit] = useState<boolean>(false);
  const [showRefetchModal, setShowRefetchModal] = useState<boolean>(false);
  const { accountBrand, updateAccountBrand } = useContext(DashboardContext);
  const { selectedAccount } = useContext(UserContext);
  const { firebaseUser, isSupportAdmin } = useContext(AuthContext);
  const { show } = useContext(SnackbarContext);
  const [buttonCSS, setButtonCSS] = useState<string>('');
  const [font, setFont] = useState<string>(accountBrand?.font ?? 'Inter');
  const [secondaryFont, setSecondaryFont] = useState<string>(
    (accountBrand as any)?.secondaryFont ?? 'Inter',
  );
  const [logo, setLogo] = useState<AssetData | undefined>(accountBrand?.logo);
  const [primaryColor, setPrimaryColor] = useState<string | undefined>(
    accountBrand?.primaryColor,
  );
  const [secondaryColor, setSecondaryColor] = useState<string | undefined>(
    accountBrand?.secondaryColor,
  );
  const [backgroundColor, setBackgroundColor] = useState<string | undefined>(
    accountBrand?.backgroundColor,
  );
  const [otherColors, setOtherColors] = useState<
    NonNullable<AccountBrand['otherColors']>
  >([]);
  const [brandGuidelines, setBrandGuidelines] = useState<GuidelineItem[]>([]);
  // const [borderRadius, setBorderRadius] = useState<number>(
  //   accountBrand?.borderRadius ?? 6,
  // );
  // const [shadow, setShadow] = useState<boolean>(accountBrand?.shadow ?? true);
  const normalizeCss = (s: string = '') => s.replace(/\s+/g, ' ').trim();

  useEffect(() => {
    // Load CSS from server when accountBrand.buttonCSS changes
    if (!accountBrand) return;

    console.log(
      'Loading CSS from server. accountBrand?.buttonCSS:',
      accountBrand?.buttonCSS,
    );

    if (!accountBrand?.buttonCSS) {
      console.log('No CSS in accountBrand, setting empty');
      setButtonCSS('');
      return;
    }

    try {
      const cssString = toCSS(accountBrand.buttonCSS as any).trim();
      setButtonCSS(cssString);
      console.log('Loaded CSS from account brand:', cssString);
    } catch (error) {
      console.error('Error converting stored CSS to string:', error);
      setButtonCSS('');
    }
  }, [accountBrand]);

  useEffect(() => {
    if (!accountBrand?.logo) return;
    setLogo(accountBrand?.logo);
  }, [accountBrand?.logo]);

  useEffect(() => {
    if (!accountBrand?.primaryColor) return;
    setPrimaryColor(accountBrand?.primaryColor);
  }, [accountBrand?.primaryColor]);

  useEffect(() => {
    if (!accountBrand?.secondaryColor) return;
    setSecondaryColor(accountBrand?.secondaryColor);
  }, [accountBrand?.secondaryColor]);

  useEffect(() => {
    if (!accountBrand?.backgroundColor) return;
    setBackgroundColor(accountBrand?.backgroundColor);
  }, [accountBrand?.backgroundColor]);

  useEffect(() => {
    if (!accountBrand?.otherColors) return;
    setOtherColors(accountBrand?.otherColors);
  }, [accountBrand?.otherColors]);

  useEffect(() => {
    if (!accountBrand?.font) return;
    setFont(accountBrand?.font);
  }, [accountBrand?.font]);

  const accountBrandSecondaryFont = (accountBrand as any)?.secondaryFont;

  useEffect(() => {
    if (accountBrandSecondaryFont) {
      setSecondaryFont(accountBrandSecondaryFont);
    }
  }, [accountBrandSecondaryFont]);

  useEffect(() => {
    if (!accountBrand?.brandGuidelines) return;
    const guidelines = Array.isArray(accountBrand.brandGuidelines)
      ? accountBrand.brandGuidelines
      : [];
    setBrandGuidelines(guidelines);
  }, [accountBrand?.brandGuidelines]);

  // useEffect(() => {
  //   if (!accountBrand?.borderRadius) return;
  //   setBorderRadius(accountBrand?.borderRadius);
  // }, [accountBrand?.borderRadius]);

  // Helper functions for managing other colors
  const updateOtherColor = useCallback((index: number, color: string) => {
    setOtherColors((prev) => {
      const newColors = [...prev];

      // If we're setting a color in the empty slot (last index), extend the array
      if (index >= newColors.length && color && color.trim() !== '') {
        newColors.push({
          id: uid(8),
          color,
          label: 'Color ' + (newColors.length + 1),
        });
      } else if (index < newColors.length) {
        if (color && color.trim() !== '') {
          newColors[index] = {
            id: newColors[index].id,
            color,
            label: newColors[index].label,
          };
        } else {
          // Remove empty colors
          newColors.splice(index, 1);
        }
      }

      return newColors;
    });
  }, []);

  const removeOtherColor = useCallback((index: number) => {
    setOtherColors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Always ensure there's one empty slot for adding new colors
  const otherColorsWithEmpty = useMemo(() => {
    const nonEmptyColors = otherColors.filter(
      (color) => color && color.color && color.color.trim() !== '',
    );
    return [...nonEmptyColors, { id: '', color: '', label: '' }]; // Empty slot
  }, [otherColors]);

  useEffect(() => {
    if (!accountBrand) return;

    // Get CSS from server with proper error handling
    let cssFromServer = '';
    try {
      cssFromServer = accountBrand.buttonCSS
        ? toCSS(accountBrand.buttonCSS as any).trim()
        : '';
    } catch (error) {
      console.error('Error converting server CSS:', error);
      cssFromServer = '';
    }

    const logoUrl = accountBrand?.logo;
    const primary =
      accountBrand?.primaryColor === undefined
        ? undefined
        : accountBrand?.primaryColor;
    const secondary =
      accountBrand?.secondaryColor === undefined
        ? undefined
        : accountBrand?.secondaryColor;
    const background =
      accountBrand?.backgroundColor === undefined
        ? undefined
        : accountBrand?.backgroundColor;
    const fontFamily =
      accountBrand?.font === undefined ? 'Inter' : accountBrand?.font;
    const secondaryFontFamily =
      (accountBrand as any)?.secondaryFont === undefined
        ? 'Inter'
        : (accountBrand as any)?.secondaryFont;
    const _brandGuidelines = Array.isArray(accountBrand?.brandGuidelines)
      ? accountBrand.brandGuidelines
      : [];
    // const radius =
    //   accountBrand?.borderRadius === undefined ? 6 : accountBrand?.borderRadius;
    // const shadowState = accountBrand?.shadow ?? true;
    const serverOtherColors = (() => {
      if (accountBrand?.otherColors) {
        try {
          const parsed =
            typeof accountBrand.otherColors === 'string'
              ? JSON.parse(accountBrand.otherColors)
              : accountBrand.otherColors;
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    })();

    const otherColorsChanged =
      JSON.stringify(otherColors) !== JSON.stringify(serverOtherColors);

    // Normalize CSS for comparison
    const currentCssNormalized = normalizeCss(buttonCSS);
    const serverCssNormalized = normalizeCss(cssFromServer);
    const cssChanged = currentCssNormalized !== serverCssNormalized;

    console.log('CSS comparison:', {
      current: `"${currentCssNormalized}"`,
      server: `"${serverCssNormalized}"`,
      currentLength: currentCssNormalized.length,
      serverLength: serverCssNormalized.length,
      changed: cssChanged,
      buttonCSS: `"${buttonCSS}"`,
      cssFromServer: `"${cssFromServer}"`,
    });

    setEdit(
      logoUrl !== logo ||
        primaryColor !== primary ||
        secondaryColor !== secondary ||
        backgroundColor !== background ||
        font !== fontFamily ||
        secondaryFont !== secondaryFontFamily ||
        JSON.stringify(brandGuidelines) !== JSON.stringify(_brandGuidelines) ||
        // borderRadius !== radius ||
        cssChanged ||
        // shadowState !== shadow ||
        otherColorsChanged,
    );
  }, [
    accountBrand,
    font,
    secondaryFont,
    logo,
    primaryColor,
    secondaryColor,
    buttonCSS,
    brandGuidelines,
    // borderRadius,
    // shadow,
    otherColors,
    backgroundColor,
  ]);

  const onDiscard = () => {
    setEdit(false);
    setLogo(accountBrand?.logo);
    setFont(accountBrand?.font ?? 'Inter');
    setSecondaryFont((accountBrand as any)?.secondaryFont ?? '');
    setPrimaryColor(accountBrand?.primaryColor);
    setSecondaryColor(accountBrand?.secondaryColor);
    setBackgroundColor(accountBrand?.backgroundColor);

    // Reset button CSS with proper error handling
    try {
      const cssFromServer = accountBrand?.buttonCSS
        ? toCSS(accountBrand.buttonCSS as any).trim()
        : '';
      setButtonCSS(cssFromServer);
    } catch (error) {
      console.error('Error resetting CSS:', error);
      setButtonCSS('');
    }

    // setBorderRadius(accountBrand?.borderRadius ?? 6);
    // setShadow(accountBrand?.shadow ?? true);
    const guidelines = Array.isArray(accountBrand?.brandGuidelines)
      ? accountBrand.brandGuidelines
      : [];
    setBrandGuidelines(guidelines);
    setOtherColors(() => {
      if (accountBrand?.otherColors) {
        try {
          const parsed =
            typeof accountBrand.otherColors === 'string'
              ? JSON.parse(accountBrand.otherColors)
              : accountBrand.otherColors;
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    });

    // Reset the initial load flag so CSS can be reloaded on next accountBrand change
  };

  const onSave = () => {
    // Helper function to clean values for Firestore
    const cleanValue = (value: any, isColor = false) => {
      if (value === undefined) {
        return undefined; // Will be filtered out
      }
      if (value === null) {
        return null;
      }
      if (value === '' && isColor) {
        return null; // Convert empty color strings to null for Firestore
      }
      if (value === '') {
        return undefined; // Will be filtered out for non-color fields
      }
      return value;
    };

    // Process buttonCSS with proper validation and conversion
    let processedButtonCSS = undefined;
    console.log('Processing buttonCSS:', {
      buttonCSS,
      trimmed: buttonCSS.trim(),
      hasContent: buttonCSS && buttonCSS.trim() !== '',
      isValid: buttonCSS ? isValidCss(buttonCSS) : 'N/A',
    });

    if (buttonCSS && buttonCSS.trim() !== '') {
      if (isValidCss(buttonCSS)) {
        try {
          processedButtonCSS = toJSON(buttonCSS);
          console.log(
            'Successfully converted CSS to JSON:',
            processedButtonCSS,
          );
        } catch (error) {
          console.error('Error converting CSS to JSON:', error);
          processedButtonCSS = undefined; // Don't save invalid CSS
        }
      } else {
        console.warn('Invalid CSS detected, not saving:', buttonCSS);
        processedButtonCSS = undefined; // Don't save invalid CSS
      }
    } else {
      // Explicitly handle empty CSS - we want to save null to clear it
      processedButtonCSS = null;
      console.log(
        'Empty CSS detected, will save as null to clear server value',
      );
    }

    // Create the update object with proper cleaning
    const rawData = {
      font: cleanValue(font),
      secondaryFont: cleanValue(secondaryFont),
      logo: cleanValue(logo),
      primaryColor: cleanValue(primaryColor, true),
      secondaryColor: cleanValue(secondaryColor, true),
      backgroundColor: cleanValue(backgroundColor, true),
      buttonCSS: processedButtonCSS,
      brandGuidelines: cleanValue(brandGuidelines),
      // borderRadius: cleanValue(borderRadius),
      // shadow: cleanValue(shadow),
      otherColors: otherColors.filter(
        (color) => color && color.color && color.color.trim() !== '',
      ),
    };

    // Filter out undefined values but keep null values (null explicitly clears fields)
    const updateData: any = {};
    Object.keys(rawData).forEach((key) => {
      const value = rawData[key as keyof typeof rawData];
      if (value !== undefined) {
        updateData[key] = value;
      }
    });

    console.log('Saving brand data:', updateData); // Debug log
    console.log('Current state before save:', {
      primaryColor,
      secondaryColor,
      backgroundColor,
      otherColors,
      buttonCSS,
      processedButtonCSS,
    });

    updateAccountBrand(updateData);

    // CSS will be reloaded automatically when accountBrand updates
  };

  const handleRefetchConfirm = async (url: string) => {
    if (!selectedAccount || !firebaseUser) return;

    try {
      await scrapeAccountData(selectedAccount.id, url, firebaseUser.uid, [
        'brand',
      ]);
      show('Brand data refresh started. This may take a minute.', 'success');
    } catch (error) {
      console.error('Error refetching brand data:', error);
      show('Failed to refresh brand data', 'warn');
      throw error;
    }
  };

  return (
    <>
      <div
        className={
          'relative grid h-full w-full max-w-full grid-rows-[1fr_auto] overflow-hidden'
        }
      >
        <div className={'h-full overflow-y-auto'}>
          <div className={'mx-auto flex max-w-161 flex-col gap-5 px-2.5 pb-5'}>
            <div className="flex items-start justify-between">
              <SettingTitle
                title="Brand Styles"
                description="Add your brand to your landing pages."
              />
              {isSupportAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRefetchModal(true)}
                >
                  Refetch Brand Data
                </Button>
              )}
            </div>

            <div className={'space-y-2'}>
              <h2 className="text-base-700 font-inter text-sm font-medium">
                Logo
              </h2>
              <FileUploadInput
                size={'small'}
                image={logo}
                onSelected={(image) => setLogo(image)}
              />
            </div>

            <div className="flex gap-6">
              <div className={'space-y-2'}>
                <h2 className="text-base-700 font-inter text-sm font-medium">
                  Primary
                </h2>
                <ColorPickerField
                  className={'rounded-2lg size-10 shadow-md'}
                  value={primaryColor}
                  debounce
                  onChange={(v) => setPrimaryColor(v)}
                />
              </div>

              <div className={'min-w-20 space-y-2'}>
                <h2 className="text-base-700 font-inter text-sm font-medium">
                  Secondary
                </h2>
                <ColorPickerField
                  className={'rounded-2lg size-10 shadow-md'}
                  value={secondaryColor}
                  debounce
                  onChange={(v) => setSecondaryColor(v)}
                />
              </div>
            </div>

            <div className={'min-w-20 space-y-2'}>
              <h2 className="text-base-700 font-inter text-sm font-medium">
                Background
              </h2>
              <ColorPickerField
                className={'rounded-2lg size-10 shadow-md'}
                value={backgroundColor}
                debounce
                onChange={(v) => setBackgroundColor(v)}
              />
            </div>

            <div className={'space-y-2'}>
              <h2 className="text-base-700 font-inter text-sm font-medium">
                Other colors
              </h2>
              <div className="flex flex-wrap gap-3">
                {otherColorsWithEmpty.map((color, index) => {
                  const isEmptySlot = color.color === '';
                  const hasDelete = !isEmptySlot && index < otherColors.length;
                  return (
                    <ColorPickerField
                      key={
                        isEmptySlot ? 'empty-slot' : `color-${color}-${index}`
                      }
                      className={'rounded-2lg size-10 shadow-md'}
                      value={color.color}
                      debounce
                      allowDelete={hasDelete}
                      onDelete={() => removeOtherColor(index)}
                      onChange={(v) => updateOtherColor(index, v)}
                    />
                  );
                })}
              </div>
            </div>

            <Autocomplete
              title="Primary Font"
              selected={font}
              onChange={(selected) => setFont(selected ?? 'Inter')}
              options={allowedFonts ?? []}
              placeholder="Select a font"
            />

            <Autocomplete
              title="Secondary Font"
              selected={secondaryFont}
              onChange={(selected) => setSecondaryFont(selected ?? 'Inter')}
              options={allowedFonts ?? []}
              placeholder="Select a secondary font"
            />

            <div className="space-y-2">
              <h2 className="text-base-700 font-inter text-sm font-medium">
                Brand Guidelines
              </h2>
              <p className="text-base-600 mb-2 text-sm">
                Provide brand guidelines, including logo usage, color schemes,
                typography, and any specific design elements.
              </p>
              <GuidelinesManager
                guidelines={brandGuidelines}
                onChange={setBrandGuidelines}
                titlePlaceholder="Guideline title (e.g., Logo Usage)"
                valuePlaceholder="Describe the guideline..."
              />
            </div>

            <div className={'space-y-1'}>
              <CodeInput
                title={'Custom Button CSS'}
                titleClassName={'text-base-700 font-inter text-sm font-medium'}
                value={buttonCSS.toString()}
                onChange={(value) => {
                  console.log('buttonCSS changed:', value);
                  setButtonCSS(value);
                  if (isValidCss(value)) {
                    console.log('updated!!!', value, toJSON(value));
                  }
                }}
                language={'css'}
                inlineLines={5}
                placeholder={
                  'text-align: center;\nfont-size: 16px;\npadding: 12px 24px;\nborder-radius: 8px;\nbackground-color: #007bff;'
                }
                // validation={{
                //   status:
                //     buttonCSS.length && !isValidCss(buttonCSS)
                //       ? 'error'
                //       : undefined,
                //   text:
                //     buttonCSS.length && !isValidCss(buttonCSS)
                //       ? 'Invalid CSS'
                //       : undefined,
                // }}
              />
            </div>

            {/* <div className={'space-y-2'}>
              <h2 className="text-base-700 font-inter text-sm font-medium">
                Border Radius
              </h2>
              <div className={'grid grid-cols-2 gap-3'}>
                <Input
                  icon={
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="32" height="32" rx="9" fill="#F2F4F7" />
                      <path
                        d="M6 6H14C20.6274 6 26 11.3726 26 18V26"
                        stroke="#637083"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  }
                  className={'font-inter rounded-2lg h-10 p-1'}
                  value={`${borderRadius}`}
                  onChange={(e) => {
                    const newVal = e.target.value;

                    try {
                      const num = parseFloat(newVal || '0');
                      setBorderRadius(num);
                    } catch (_) {
                    }
                  }}
                />
                <Slider
                  min={0}
                  max={54}
                  step={0.5}
                  value={[borderRadius]}
                  onValueChange={(value: number[]) => setBorderRadius(value[0])}
                />
              </div>
            </div> */}

            {/* <div className="flex items-center space-x-2">
              <Switch
                id="show-shadow"
                checked={shadow}
                onCheckedChange={(checked) => setShadow(checked)}
              />
              <Label
                htmlFor="show-shadow"
                className={'font-inter text-base font-normal'}
              >
                Show Shadows
              </Label>
            </div> */}
          </div>
        </div>
      </div>
      {edit && (
        <div className={'w-full'}>
          <span
            className={
              'block h-px w-full bg-[linear-gradient(90deg,rgba(228,231,236,0)_0%,#E4E7EC_50.35%,rgba(228,231,236,0)_100%)]'
            }
          />
          <div
            className={
              'mx-auto flex w-full max-w-161 justify-end gap-3 bg-white px-2.5 pt-2'
            }
          >
            <Button variant={'outline'} onClick={onDiscard}>
              Discard
            </Button>
            <Button onClick={onSave}>Save</Button>
          </div>
        </div>
      )}
      <RefetchAccountDataModal
        open={showRefetchModal}
        onClose={() => setShowRefetchModal(false)}
        onConfirm={handleRefetchConfirm}
        title="Refetch Brand Data"
        description="Enter the website URL to scrape brand information from."
        defaultUrl={selectedAccount?.data()?.websiteUrl || ''}
      />
    </>
  );
};
